'use client';

/**
 * FieldMappingTable Component
 *
 * Source to target field mapping table for the ImportWizard.
 * Provides auto-suggestions for field mappings based on column names.
 */

import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Select, { type SelectChangeEvent } from '@mui/material/Select';
import { alpha, styled, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { motion } from 'framer-motion';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useI18n } from '../../hooks/useI18n';
import { getNeonStyles, type NeonAccent } from '../../theme/palette';
import { glassEffects } from '../../theme/shadows';
import { transitionPresets } from '../../theme/transitions';
import type { FieldDefinition, FieldMapping } from '../../types/entityFramework';
import { GlassTooltip } from '../GlassTooltip';

// ============================================================================
// Types
// ============================================================================

export interface FieldMappingTableProps {
  /**
   * Source columns from the uploaded file
   */
  sourceColumns: string[];

  /**
   * Target entity fields to map to
   */
  targetFields: FieldDefinition[];

  /**
   * Current field mappings
   */
  mappings: FieldMapping[];

  /**
   * Callback when mappings change
   */
  onMappingsChange: (mappings: FieldMapping[]) => void;

  /**
   * File preview data (first few rows)
   */
  previewData?: Record<string, unknown>[];

  /**
   * Neon accent color
   * @default "cyan"
   */
  neonAccent?: NeonAccent;

  /**
   * Additional class name
   */
  className?: string;
}

// ============================================================================
// Styled Components
// ============================================================================

const StyledTable = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<{ $isDark: boolean }>(({ $isDark }) => {
  const glass = $isDark ? glassEffects.dark : glassEffects.light;

  return {
    width: '100%',
    borderRadius: 12,
    overflow: 'hidden',
    border: `1px solid ${glass.border}`,
    backgroundColor: glass.background,
    backdropFilter: glass.backdropBlur,
  };
});

const StyledHeaderRow = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<{
  $isDark: boolean;
  $neonAccent: NeonAccent;
}>(({ $isDark, $neonAccent }) => {
  const neonStyles = getNeonStyles($neonAccent, $isDark);

  return {
    display: 'grid',
    gridTemplateColumns: '1fr 48px 1fr 120px',
    gap: 16,
    padding: '12px 16px',
    alignItems: 'center',
    backgroundColor: alpha(neonStyles.base, $isDark ? 0.08 : 0.04),
    borderBottom: `1px solid ${$isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
  };
});

const StyledRow = styled(motion.div, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<{ $isDark: boolean; $isOdd: boolean }>(({ $isDark, $isOdd }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 48px 1fr 120px',
  gap: 16,
  padding: '12px 16px',
  alignItems: 'center',
  backgroundColor: $isOdd ? ($isDark ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.01)') : 'transparent',
  borderBottom: `1px solid ${$isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
  transition: transitionPresets.allFast,

  '&:last-child': {
    borderBottom: 'none',
  },

  '&:hover': {
    backgroundColor: $isDark ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.02)',
  },
}));

const StyledSourceColumn = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  minWidth: 0,
});

const StyledPreviewValue = styled(Typography, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<{ $isDark: boolean }>(({ $isDark }) => ({
  fontSize: '0.75rem',
  color: $isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  maxWidth: 200,
}));

const StyledTransformSelect = styled(Select, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<{ $isDark: boolean }>(({ $isDark }) => ({
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: $isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: $isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
  },
  '& .MuiSelect-select': {
    padding: '8px 12px',
    fontSize: '0.8125rem',
  },
}));

const StyledStatusIcon = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<{
  $status: 'mapped' | 'unmapped' | 'required';
}>(({ theme, $status }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color:
    $status === 'mapped'
      ? theme.palette.success.main
      : $status === 'required'
        ? theme.palette.warning.main
        : theme.palette.text.disabled,
}));

// ============================================================================
// Helpers
// ============================================================================

/**
 * Calculate similarity between two strings (for auto-suggestion)
 */
const calculateSimilarity = (str1: string, str2: string): number => {
  const s1 = str1.toLowerCase().replace(/[_\s-]/g, '');
  const s2 = str2.toLowerCase().replace(/[_\s-]/g, '');

  if (s1 === s2) return 1;
  if (s1.includes(s2) || s2.includes(s1)) return 0.8;

  // Levenshtein distance for fuzzy matching
  const matrix: number[][] = [];
  for (let i = 0; i <= s1.length; i++) {
    matrix[i] = [i];
  }
  for (let j = 0; j <= s2.length; j++) {
    matrix[0][j] = j;
  }
  for (let i = 1; i <= s1.length; i++) {
    for (let j = 1; j <= s2.length; j++) {
      const cost = s1[i - 1] === s2[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(matrix[i - 1][j] + 1, matrix[i][j - 1] + 1, matrix[i - 1][j - 1] + cost);
    }
  }

  const maxLen = Math.max(s1.length, s2.length);
  return maxLen === 0 ? 1 : 1 - matrix[s1.length][s2.length] / maxLen;
};

/**
 * Find the best matching target field for a source column
 */
const findBestMatch = (
  sourceColumn: string,
  targetFields: FieldDefinition[],
  existingMappings: FieldMapping[],
): string | null => {
  const mappedTargets = new Set(existingMappings.map((m) => m.targetField));

  let bestMatch: string | null = null;
  let bestScore = 0;

  for (const field of targetFields) {
    if (mappedTargets.has(field.name)) continue;

    const nameScore = calculateSimilarity(sourceColumn, field.name);
    const labelScore = calculateSimilarity(sourceColumn, field.label);
    const score = Math.max(nameScore, labelScore);

    if (score > bestScore && score >= 0.5) {
      bestScore = score;
      bestMatch = field.name;
    }
  }

  return bestMatch;
};

const TRANSFORM_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'uppercase', label: 'Uppercase' },
  { value: 'lowercase', label: 'Lowercase' },
  { value: 'trim', label: 'Trim' },
  { value: 'date', label: 'Parse Date' },
  { value: 'number', label: 'Parse Number' },
  { value: 'boolean', label: 'Parse Boolean' },
];

// ============================================================================
// Component
// ============================================================================

export const FieldMappingTable: React.FC<FieldMappingTableProps> = ({
  sourceColumns,
  targetFields,
  mappings,
  onMappingsChange,
  previewData = [],
  neonAccent = 'cyan',
  className,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { t } = useI18n();
  const neonStyles = getNeonStyles(neonAccent, isDark);

  // Get the current mapping for a source column
  const getMappingForSource = useCallback(
    (sourceColumn: string): FieldMapping | undefined => {
      return mappings.find((m) => m.sourceColumn === sourceColumn);
    },
    [mappings],
  );

  // Handle target field selection
  const handleTargetChange = useCallback(
    (sourceColumn: string, targetField: string) => {
      const existingIndex = mappings.findIndex((m) => m.sourceColumn === sourceColumn);
      const newMappings = [...mappings];

      if (targetField === '') {
        // Remove mapping
        if (existingIndex !== -1) {
          newMappings.splice(existingIndex, 1);
        }
      } else {
        // Add or update mapping
        const newMapping: FieldMapping = {
          sourceColumn,
          targetField,
          transform: getMappingForSource(sourceColumn)?.transform || 'none',
        };

        if (existingIndex !== -1) {
          newMappings[existingIndex] = newMapping;
        } else {
          newMappings.push(newMapping);
        }
      }

      onMappingsChange(newMappings);
    },
    [mappings, onMappingsChange, getMappingForSource],
  );

  // Handle transform change
  const handleTransformChange = useCallback(
    (sourceColumn: string, transform: FieldMapping['transform']) => {
      const existingIndex = mappings.findIndex((m) => m.sourceColumn === sourceColumn);
      if (existingIndex === -1) return;

      const newMappings = [...mappings];
      newMappings[existingIndex] = {
        ...newMappings[existingIndex],
        transform,
      };

      onMappingsChange(newMappings);
    },
    [mappings, onMappingsChange],
  );

  // Auto-map all fields
  const handleAutoMap = useCallback(() => {
    const newMappings: FieldMapping[] = [];
    const usedTargets = new Set<string>();

    for (const sourceColumn of sourceColumns) {
      const existingMapping = getMappingForSource(sourceColumn);
      if (existingMapping) {
        newMappings.push(existingMapping);
        usedTargets.add(existingMapping.targetField);
        continue;
      }

      const remainingFields = targetFields.filter((f) => !usedTargets.has(f.name));
      const bestMatch = findBestMatch(sourceColumn, remainingFields, []);

      if (bestMatch) {
        newMappings.push({
          sourceColumn,
          targetField: bestMatch,
          transform: 'none',
        });
        usedTargets.add(bestMatch);
      }
    }

    onMappingsChange(newMappings);
  }, [sourceColumns, targetFields, getMappingForSource, onMappingsChange]);

  // Get preview value for a column
  const getPreviewValue = (sourceColumn: string): string => {
    if (previewData.length === 0) return '';
    const value = previewData[0][sourceColumn];
    if (value === null || value === undefined) return '(empty)';
    return String(value);
  };

  // Get unmapped required fields
  const unmappedRequiredFields = useMemo(() => {
    const mappedTargets = new Set(mappings.map((m) => m.targetField));
    return targetFields.filter((f) => f.required && !mappedTargets.has(f.name));
  }, [targetFields, mappings]);

  // Track if auto-map has already run (prevents infinite loop with deps)
  const hasAutoMapped = useRef(false);

  // Auto-suggest on initial mount if no mappings exist
  useEffect(() => {
    if (!hasAutoMapped.current && mappings.length === 0 && sourceColumns.length > 0) {
      hasAutoMapped.current = true;
      handleAutoMap();
    }
  }, [mappings.length, sourceColumns.length, handleAutoMap]);

  return (
    <Box className={className}>
      {/* Auto-map button and unmapped required warning */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
            }}
          >
            {t('importWizard.columnsMapped', { mapped: mappings.length, total: sourceColumns.length })}
          </Typography>
          {unmappedRequiredFields.length > 0 && (
            <GlassTooltip
              title={`Required fields not mapped: ${unmappedRequiredFields.map((f) => f.label).join(', ')}`}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  color: 'warning.main',
                }}
              >
                <WarningIcon fontSize="small" />
                <Typography variant="caption">
                  {t('importWizard.requiredFieldsMissing', { count: unmappedRequiredFields.length })}
                </Typography>
              </Box>
            </GlassTooltip>
          )}
        </Box>

        <GlassTooltip title={t('importWizard.autoMapTooltip')}>
          <IconButton
            onClick={handleAutoMap}
            size="small"
            sx={{
              color: neonStyles.base,
              '&:hover': {
                backgroundColor: alpha(neonStyles.base, 0.1),
              },
            }}
          >
            <AutoFixHighIcon />
          </IconButton>
        </GlassTooltip>
      </Box>
      {/* Mapping table */}
      <StyledTable $isDark={isDark}>
        <StyledHeaderRow $isDark={isDark} $neonAccent={neonAccent}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {t('importWizard.sourceColumn')}
          </Typography>
          <Box /> {/* Arrow spacer */}
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {t('importWizard.targetField')}
          </Typography>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {t('importWizard.transform')}
          </Typography>
        </StyledHeaderRow>

        {sourceColumns.map((sourceColumn, index) => {
          const mapping = getMappingForSource(sourceColumn);
          const isMapped = !!mapping?.targetField;
          const targetField = targetFields.find((f) => f.name === mapping?.targetField);
          const isRequiredField = targetField?.required;

          return (
            <StyledRow
              key={sourceColumn}
              $isDark={isDark}
              $isOdd={index % 2 === 1}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.02 }}
            >
              {/* Source column info */}
              <StyledSourceColumn>
                <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                  {sourceColumn}
                </Typography>
                <StyledPreviewValue $isDark={isDark}>Preview: {getPreviewValue(sourceColumn)}</StyledPreviewValue>
              </StyledSourceColumn>
              {/* Arrow with status */}
              <StyledStatusIcon $status={isMapped ? 'mapped' : isRequiredField ? 'required' : 'unmapped'}>
                {isMapped ? <CheckCircleIcon fontSize="small" /> : <ArrowForwardIcon fontSize="small" />}
              </StyledStatusIcon>
              {/* Target field selector */}
              <FormControl size="small" fullWidth>
                <Select
                  value={mapping?.targetField || ''}
                  onChange={(e: SelectChangeEvent<string>) => handleTargetChange(sourceColumn, e.target.value)}
                  displayEmpty
                  MenuProps={{
                    slotProps: {
                      backdrop: { invisible: true },
                    },
                  }}
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: neonStyles.base,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: neonStyles.base,
                    },
                    '& .MuiSelect-select': {
                      py: 1,
                    },
                  }}
                >
                  <MenuItem value="">
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                      }}
                    >
                      {t('importWizard.skipColumn')}
                    </Typography>
                  </MenuItem>
                  {targetFields.map((field) => {
                    const isAlreadyMapped = mappings.some(
                      (m) => m.targetField === field.name && m.sourceColumn !== sourceColumn,
                    );
                    return (
                      <MenuItem key={field.name} value={field.name} disabled={isAlreadyMapped}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2">
                            {field.label}
                            {field.required && (
                              <Typography component="span" color="error" sx={{ ml: 0.5 }}>
                                *
                              </Typography>
                            )}
                          </Typography>
                          {isAlreadyMapped && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'text.disabled',
                              }}
                            >
                              {t('importWizard.alreadyMapped')}
                            </Typography>
                          )}
                        </Box>
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
              {/* Transform selector */}
              <FormControl size="small" fullWidth disabled={!isMapped}>
                <StyledTransformSelect
                  value={mapping?.transform || 'none'}
                  onChange={(e: SelectChangeEvent<unknown>) =>
                    handleTransformChange(sourceColumn, e.target.value as FieldMapping['transform'])
                  }
                  $isDark={isDark}
                  disabled={!isMapped}
                >
                  {TRANSFORM_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      <Typography variant="body2">{option.label}</Typography>
                    </MenuItem>
                  ))}
                </StyledTransformSelect>
              </FormControl>
            </StyledRow>
          );
        })}
      </StyledTable>
    </Box>
  );
};

FieldMappingTable.displayName = 'FieldMappingTable';

export default FieldMappingTable;
